"use client";

import React from "react";
import { Text, SectionCard, CardTitle, Section } from "@/src/components";
import { ChartProvider } from "@/src/components/charts/chart-provider";
import { TreemapChart } from "@/src/components/charts/treemap-chart";

const departmentSpendBasic = [
  {
    name: "Engineering",
    value: 125000,
    children: [
      { name: "Salaries", value: 85000 },
      { name: "Infrastructure", value: 25000 },
      { name: "Tools & Software", value: 15000 },
    ],
  },
  {
    name: "Sales",
    value: 95000,
    children: [
      { name: "Commissions", value: 50000 },
      { name: "Travel", value: 30000 },
      { name: "Marketing Materials", value: 15000 },
    ],
  },
  {
    name: "Marketing",
    value: 75000,
    children: [
      { name: "Digital Ads", value: 40000 },
      { name: "Content Creation", value: 20000 },
      { name: "Events", value: 15000 },
    ],
  },
  {
    name: "Operations",
    value: 60000,
    children: [
      { name: "Facilities", value: 35000 },
      { name: "Logistics", value: 15000 },
      { name: "Admin", value: 10000 },
    ],
  },
  {
    name: "HR",
    value: 45000,
    children: [
      { name: "Recruitment", value: 20000 },
      { name: "Training", value: 15000 },
      { name: "Benefits", value: 10000 },
    ],
  },
];

const glAccountCategories = [
  { name: "5100 - Personnel", value: 245000 },
  { name: "5200 - Occupancy", value: 85000 },
  { name: "5300 - Equipment", value: 62000 },
  { name: "5400 - Services", value: 48000 },
  { name: "5500 - Travel", value: 35000 },
  { name: "5600 - Marketing", value: 42000 },
  { name: "5700 - Technology", value: 38000 },
  { name: "5800 - Other", value: 25000 },
];

const hierarchicalCosts = [
  {
    name: "Product Development",
    value: 320000,
    children: [
      {
        name: "Core Engineering",
        value: 180000,
        children: [
          { name: "Backend", value: 90000 },
          { name: "Frontend", value: 60000 },
          { name: "DevOps", value: 30000 },
        ],
      },
      {
        name: "QA & Testing",
        value: 80000,
        children: [
          { name: "Manual Testing", value: 35000 },
          { name: "Automation", value: 30000 },
          { name: "Performance", value: 15000 },
        ],
      },
      {
        name: "Design & UX",
        value: 60000,
        children: [
          { name: "UI Design", value: 35000 },
          { name: "UX Research", value: 25000 },
        ],
      },
    ],
  },
  {
    name: "Go-to-Market",
    value: 210000,
    children: [
      {
        name: "Sales",
        value: 120000,
        children: [
          { name: "Inside Sales", value: 60000 },
          { name: "Field Sales", value: 40000 },
          { name: "Sales Operations", value: 20000 },
        ],
      },
      {
        name: "Marketing",
        value: 90000,
        children: [
          { name: "Demand Gen", value: 40000 },
          { name: "Content", value: 30000 },
          { name: "Brand", value: 20000 },
        ],
      },
    ],
  },
  {
    name: "Infrastructure & Ops",
    value: 160000,
    children: [
      {
        name: "Cloud & Hosting",
        value: 85000,
      },
      {
        name: "Operations",
        value: 75000,
        children: [
          { name: "Finance", value: 30000 },
          { name: "HR", value: 25000 },
          { name: "Legal & Compliance", value: 20000 },
        ],
      },
    ],
  },
];

export default function DocsTreemapChartPage() {
  return (
    <ChartProvider>
      <div className="space-y-theme-5 overflow-auto h-full py-theme-6 px-theme-6">
        {/* Header */}
        <Section>
          <div className="space-y-2">
            <Text as="h1" className="text-2xl font-bold tracking-tight">
              Treemap Chart
            </Text>
            <Text className="text-base text-muted-foreground max-w-3xl">
              Hierarchical visualization for displaying nested data with
              proportional rectangles. Perfect for analyzing organizational
              spend, budget allocation, and hierarchical cost breakdowns.
            </Text>
          </div>
        </Section>

        {/* Basic Example */}
        <Section title="Basic Example">
          <SectionCard
            header={<CardTitle>Department Spend Breakdown</CardTitle>}
            contentClassName="p-theme-5"
          >
            <TreemapChart
              data={departmentSpendBasic}
              height={400}
              showLabels={true}
              showBreadcrumb={true}
            />
          </SectionCard>
        </Section>

        {/* Flat Treemap */}
        <Section title="Flat Treemap">
          <SectionCard
            header={<CardTitle>GL Account Categories</CardTitle>}
            contentClassName="p-theme-5"
          >
            <TreemapChart
              data={glAccountCategories}
              height={350}
              showLabels={true}
              showBreadcrumb={false}
              levels={1}
            />
          </SectionCard>
        </Section>

        {/* Hierarchical */}
        <Section title="Hierarchical Breakdown">
          <SectionCard
            header={<CardTitle>Multi-Level Cost Structure</CardTitle>}
            contentClassName="p-theme-5"
          >
            <TreemapChart
              data={hierarchicalCosts}
              height={450}
              showLabels={true}
              showBreadcrumb={true}
              levels={3}
            />
          </SectionCard>
        </Section>

        {/* With Custom Formatting */}
        <Section title="Variants">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-theme-4">
            <SectionCard
              header={<CardTitle>Without Breadcrumb</CardTitle>}
              contentClassName="p-theme-5"
            >
              <TreemapChart
                data={departmentSpendBasic}
                height={300}
                showLabels={true}
                showBreadcrumb={false}
              />
            </SectionCard>

            <SectionCard
              header={<CardTitle>With Value Formatting</CardTitle>}
              contentClassName="p-theme-5"
            >
              <TreemapChart
                data={departmentSpendBasic}
                height={300}
                showLabels={true}
                showBreadcrumb={true}
                formatValue={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
            </SectionCard>
          </div>
        </Section>

        {/* Props Table */}
        <Section title="Props">
          <SectionCard
            header={<CardTitle>TreemapChartProps</CardTitle>}
            contentClassName="p-theme-5 overflow-x-auto"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-semibold">Property</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Default</th>
                  <th className="text-left py-2 px-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">data</td>
                  <td className="py-2 px-2">TreemapNode[]</td>
                  <td className="py-2 px-2">Required</td>
                  <td className="py-2 px-2">Hierarchical array of nodes with name, value, and optional children</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">title</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart title</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">subtitle</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Chart subtitle</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">height</td>
                  <td className="py-2 px-2">string | number</td>
                  <td className="py-2 px-2">400</td>
                  <td className="py-2 px-2">Chart height in px or CSS value</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showBreadcrumb</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Show breadcrumb navigation for hierarchy</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">showLabels</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">true</td>
                  <td className="py-2 px-2">Show labels on rectangles</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">levels</td>
                  <td className="py-2 px-2">number</td>
                  <td className="py-2 px-2">2</td>
                  <td className="py-2 px-2">Maximum hierarchy levels to display</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">className</td>
                  <td className="py-2 px-2">string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Additional CSS classes</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">loading</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show loading state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">empty</td>
                  <td className="py-2 px-2">boolean</td>
                  <td className="py-2 px-2">false</td>
                  <td className="py-2 px-2">Show empty state</td>
                </tr>
                <tr className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">formatValue</td>
                  <td className="py-2 px-2">(value: number) =&gt; string</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Format values in labels and tooltips</td>
                </tr>
                <tr className="hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono">onNodeClick</td>
                  <td className="py-2 px-2">(params: any) =&gt; void</td>
                  <td className="py-2 px-2">—</td>
                  <td className="py-2 px-2">Click event handler for nodes</td>
                </tr>
              </tbody>
            </table>
          </SectionCard>
        </Section>

        {/* Data Structure */}
        <Section title="Data Structure">
          <SectionCard
            header={<CardTitle>TreemapNode Interface</CardTitle>}
            contentClassName="p-theme-5"
          >
            <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
              <code>{`interface TreemapNode {
  name: string;                    // Node label
  value: number;                   // Numeric value for rectangle size
  children?: TreemapNode[];        // Optional nested nodes (for hierarchy)
  color?: string;                  // Optional custom color (hex/CSS)
}

// Flat example (single level):
const flatData: TreemapNode[] = [
  { name: "Engineering", value: 125000 },
  { name: "Sales", value: 95000 },
  { name: "Marketing", value: 75000 }
];

// Hierarchical example (multi-level):
const hierarchicalData: TreemapNode[] = [
  {
    name: "Engineering",
    value: 125000,
    children: [
      { name: "Salaries", value: 85000 },
      { name: "Infrastructure", value: 25000 },
      { name: "Tools & Software", value: 15000 }
    ]
  },
  {
    name: "Sales",
    value: 95000,
    children: [
      { name: "Commissions", value: 50000 },
      { name: "Travel", value: 30000 },
      { name: "Marketing Materials", value: 15000 }
    ]
  }
];

// Usage with formatting:
<TreemapChart
  data={hierarchicalData}
  height={450}
  showLabels={true}
  showBreadcrumb={true}
  levels={3}
  formatValue={(value) => \`$\${(value / 1000).toFixed(0)}k\`}
  onNodeClick={(params) => console.log('Clicked:', params)}
/>`}</code>
            </pre>
          </SectionCard>
        </Section>

        {/* Usage Examples */}
        <Section title="Usage Examples">
          <div className="space-y-theme-4">
            <SectionCard
              header={<CardTitle>Budget Allocation View</CardTitle>}
              contentClassName="p-theme-5"
            >
              <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
                <code>{`import { TreemapChart } from "@/src/components/charts/treemap-chart";

const budgetData = [
  {
    name: "Operating",
    value: 500000,
    children: [
      { name: "Personnel", value: 300000 },
      { name: "Facilities", value: 150000 },
      { name: "Equipment", value: 50000 }
    ]
  },
  {
    name: "R&D",
    value: 250000,
    children: [
      { name: "Research", value: 150000 },
      { name: "Prototyping", value: 100000 }
    ]
  }
];

export function BudgetView() {
  return (
    <TreemapChart
      data={budgetData}
      height={400}
      title="Annual Budget Allocation"
      subtitle="Total Budget: $750,000"
      showBreadcrumb={true}
      formatValue={(v) => \`$\${(v / 1000).toFixed(0)}k\`}
    />
  );
}`}</code>
              </pre>
            </SectionCard>

            <SectionCard
              header={<CardTitle>Interactive Cost Drilldown</CardTitle>}
              contentClassName="p-theme-5"
            >
              <pre className="bg-muted p-theme-3 rounded-lg text-xs overflow-x-auto">
                <code>{`import { TreemapChart } from "@/src/components/charts/treemap-chart";
import { useState } from "react";

export function CostDrilldown() {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  const costData = [
    {
      name: "Technology",
      value: 450000,
      children: [
        { name: "Cloud Services", value: 200000 },
        { name: "Software Licenses", value: 150000 },
        { name: "Infrastructure", value: 100000 }
      ]
    },
    {
      name: "People",
      value: 800000,
      children: [
        { name: "Salaries", value: 600000 },
        { name: "Benefits", value: 150000 },
        { name: "Training", value: 50000 }
      ]
    }
  ];

  const handleNodeClick = (params: any) => {
    setSelectedNode(params.name);
    console.log("Selected node:", params);
  };

  return (
    <>
      <TreemapChart
        data={costData}
        height={400}
        onNodeClick={handleNodeClick}
        formatValue={(v) => \`$\${v.toLocaleString()}\`}
      />
      {selectedNode && (
        <div className="mt-4 p-4 bg-info-bg rounded">
          Selected: {selectedNode}
        </div>
      )}
    </>
  );
}`}</code>
              </pre>
            </SectionCard>
          </div>
        </Section>
      </div>
    </ChartProvider>
  );
}

export { DocsTreemapChartPage as TreemapChartPage };
